import { Button, IconButton, legacyColors, primaryColors, secondaryColors } from '@tryhackme/thm-ui-components';
import styled, { css } from 'styled-components';

import { ReactComponent as Crown } from 'src/images/svgs/subscriptions/crown.svg';

const sliderTrackStyles = css`
  height: 0.8rem;
  border-radius: ${({ theme }) => theme.radius.small};
  background: linear-gradient(to right, #8dd11b, #8dd11b var(--value), ${secondaryColors.grey[800]} var(--value));
`;

const sliderThumbStyles = css`
  background: ${primaryColors.main};
  height: 2rem;
  width: 2rem;
  border-radius: 50%;
`;

const sliderThumbFocusStyles = css`
  border: 0.1rem solid ${secondaryColors.white};
`;

const labelStyles = css`
  font-weight: ${({ theme }) => theme.fonts.weight.bolder};
`;

const valueCardStyles = css`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background: ${secondaryColors.black};
  border-radius: ${({ theme }) => theme.radius.small};
  border: 0.1rem solid ${secondaryColors.secondary.lighter};
`;

const valueTextStyles = css`
  color: ${secondaryColors.grey[100]};
  font-size: ${({ theme }) => theme.fonts.sizes.medium};
  line-height: 1.9rem;
  margin-bottom: 0;
`;

export const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  padding: 2.4rem 1.8rem;

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    padding: 3.2rem;
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.lg}) {
    flex-direction: row;
  }
`;

export const StyledYourVoucherCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 57rem;
  padding: 3.2rem 3.2rem 9.6rem 3.2rem;
  background: ${secondaryColors.secondary.light};
  color: ${secondaryColors.grey[100]};
  border-radius: 3.2rem;

  h2 {
    color: ${primaryColors.main};
    margin-bottom: 0;
  }
`;

export const StyledYourVoucherCardTitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: 2.4rem;
  color: ${secondaryColors.grey[100]};
  margin-bottom: 3.2rem;
`;

export const StyledSliderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.6rem;
  margin-bottom: 1.6rem;

  p:first-of-type {
    ${labelStyles};
  }

  P:last-of-type {
    display: flex;
    align-items: center;
    gap: 0.8rem;

    span {
      font-family: ${({ theme }) => theme.fonts.familyUbuntu};
      font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
      font-size: 2.4rem;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 3.6rem;
      height: 4.4rem;
      padding: 1rem 1.2rem;
      color: ${primaryColors.main};
      background: ${secondaryColors.black};
      border-radius: ${({ theme }) => theme.radius.small};
      border: 0.1rem solid ${secondaryColors.secondary.lighter};
    }
  }
`;

export const StyledSlider = styled.input`
  width: 100%;
  max-width: 22.3rem;
  min-width: 14.8rem;

  /* Reset defaults */
  &[type='range'] {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
  }

  /* Removes default focus */
  &[type='range']:focus {
    outline: none;
  }

  /* Chrome, Safari, Opera and Edge Chromium styles */
  /* slider track */
  &[type='range']::-webkit-slider-runnable-track {
    ${sliderTrackStyles};
  }

  /* slider thumb */
  &[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    margin-top: -0.65rem;
    ${sliderThumbStyles};
  }

  /* Add custom focus */
  &[type='range']:focus::-webkit-slider-thumb {
    ${sliderThumbFocusStyles};
  }

  /******** Firefox styles ********/
  /* slider track */
  &[type='range']::-moz-range-track {
    ${sliderTrackStyles};
  }

  /* slider thumb */
  &[type='range']::-moz-range-thumb {
    border: none; /* Removes extra border that FF applies */
    border-radius: 0; /* Removes default border-radius that FF applies */
    ${sliderThumbStyles};
  }

  &[type='range']:focus::-moz-range-thumb {
    ${sliderThumbFocusStyles};
  }
`;

export const StyledIconButton = styled(IconButton)`
  color: ${secondaryColors.grey[100]};
  border: 0.1rem solid ${primaryColors.main};
`;

export const StyledLabel = styled.p`
  ${labelStyles};
`;

export const StyledSubscriptions = styled.div`
  ${valueCardStyles};
  width: 5.6rem;
  height: 3.6rem;
  padding: 1rem 1.6rem 1rem 1.2rem;

  p {
    ${valueTextStyles};
    font-weight: ${({ theme }) => theme.fonts.weight.regular};
  }
`;

export const StyledVoucherText = styled.p`
  display: none;

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    display: block;
  }
`;

export const StyledYourVoucherCardPrice = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    91deg,
    ${legacyColors.misc.darkLoadingPrimary} 1.23%,
    ${legacyColors.misc.darkLoadingPrimary} 49.15%,
    ${legacyColors.misc.darkLoadingPrimary} 99.11%
  );
  padding: 1.6rem 4rem;
  border-radius: 0 0 3.2rem 3.2rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 2.4rem;
`;

export const StyledRadioGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
`;

export const StyledDivider = styled.div`
  background: ${secondaryColors.grey[300]};
  width: 100%;
  height: 0.1rem;
  margin: 2.4rem 0;

  @media screen and (min-width: ${({ theme }) => theme.sizes.lg}) {
    width: 0.1rem;
    height: auto;
    margin: 0 6.5rem;
  }
`;

export const StyledSummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const StyledSummary = styled.div<{ $isDarkMode: boolean }>`
  p {
    font-size: ${({ theme }) => theme.fonts.sizes.medium};
    color: ${({ $isDarkMode }) => ($isDarkMode ? secondaryColors.grey[100] : '#000')};
    display: inline;
    margin-right: 0.6rem;
  }
`;

export const StyledValue = styled.span`
  ${valueCardStyles};
  ${valueTextStyles};
  width: fit-content;
  height: 3.2rem;
  margin: 0.1rem 0.8rem 0.1rem 0;
  padding: 1rem 1.2rem;
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
`;

export const StyledInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 1.6rem;
  margin-top: 1.6rem;

  div:first-of-type {
    width: 100%;
    label {
      font-size: ${({ theme }) => theme.fonts.sizes.default};
      margin-right: 0.8rem;
    }
    input {
      min-width: unset;
    }

    svg {
      margin-left: auto;
    }
  }

  p[role='alert'] {
    font-size: ${({ theme }) => theme.fonts.sizes.default};
    color: ${({ theme }) => theme.colors.error};
    margin-top: 0;
  }
`;

export const StyledButton = styled(Button)`
  svg {
    width: 1.8rem;
    height: 1.9rem;
  }
`;

export const StyledCrownSVG = styled(Crown)`
  position: absolute;
  top: -4.5rem;
  right: -4rem;
  width: 9.2rem;
`;
